/**
 * P440.4 -- the adopted MVB target.
 *
 * The derived target above moves with the estate; this one does not. It
 * is a plan frozen on the day it was proposed, signed by the CRO and
 * the Head of Operational Resilience, and sealed on the second
 * signature. Progress against it is read from the estate as it is now:
 * a step is done only when the gap it addressed is closed.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api, currentAuthToken, type ApiError } from "shared/api/client";
import { holdsRealmRole, useMe } from "shared/api/useMe";
import { formatSAR } from "shared/money/money";
import { useToast } from "shared/toast/ToastProvider";

const P = "targetState.adopt";
const KEY = ["twin", "mvb-target-adopted"] as const;

interface ProgressStep {
  kind: string;
  target: string;
  cost_sar: string | null;
  done: boolean;
}

export interface AdoptedTarget {
  id: string;
  version: number;
  workflow_state: "draft" | "cro_signed" | "ops_res_signed" | "approved";
  derived_on: string;
  total_sar: string;
  at_least: boolean;
  rationale: string | null;
  cro_signoff_name: string | null;
  ops_res_signoff_name: string | null;
  progress: {
    steps: ProgressStep[];
    done: number;
    total: number;
    done_sar: string;
    time_to_minimum_viability_hours: number;
    adopted_before_hours: number;
    adopted_target_hours: number;
  };
}

/** The signed pack (P440.4): fetched as a blob, the war-room pack shape. */
async function downloadPack(): Promise<void> {
  const token = currentAuthToken();
  const res = await fetch("/v1/admin/exports/mvb-target-pack.zip", {
    headers: {
      Accept: "application/zip",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    let detail = res.statusText || `HTTP ${res.status}`;
    try {
      detail = ((await res.json()) as { detail?: string })?.detail ?? detail;
    } catch {
      // non-JSON body; keep statusText
    }
    throw new Error(detail);
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = "marsad-mvb-target-pack.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

function isAdopted(x: unknown): x is AdoptedTarget {
  return (
    !!x &&
    typeof (x as AdoptedTarget).workflow_state === "string" &&
    Array.isArray((x as AdoptedTarget).progress?.steps)
  );
}

export function AdoptedTargetPanel({ budget }: { budget: string | null }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const me = useMe().data;
  const [rationale, setRationale] = useState("");
  const q = useQuery<AdoptedTarget | null, ApiError>({
    queryKey: KEY,
    queryFn: () => api.get<AdoptedTarget | null>("/v1/twin/mvb/target"),
  });
  const done = () => void qc.invalidateQueries({ queryKey: KEY });
  const propose = useMutation<unknown, ApiError>({
    meta: { handled: true },
    mutationFn: () =>
      api.post("/v1/twin/mvb/target", {
        budget_sar: budget,
        rationale: rationale.trim() || null,
      }),
    onSuccess: () => {
      toast.push({ tone: "success", message: t(`${P}.proposed`) });
      setRationale("");
      done();
    },
    onError: (e) => toast.push({ tone: "error", message: e.detail }),
  });
  const sign = useMutation<unknown, ApiError, "cro" | "ops_res">({
    meta: { handled: true },
    mutationFn: (role) => api.post("/v1/twin/mvb/target/signoff", { role }),
    onSuccess: () => {
      toast.push({ tone: "success", message: t(`${P}.signed`) });
      done();
    },
    onError: (e) => toast.push({ tone: "error", message: e.detail }),
  });

  const pack = useMutation<void, Error>({
    meta: { handled: true },
    mutationFn: downloadPack,
    onError: (e) => toast.push({ tone: "error", message: e.message }),
  });

  const target = isAdopted(q.data) ? q.data : null;
  const canPropose = !!me && (me.is_admin || holdsRealmRole(me, "head_of_opsres"));
  const canCro = holdsRealmRole(me, "cro") && !target?.cro_signoff_name;
  const canOps = holdsRealmRole(me, "head_of_opsres") && !target?.ops_res_signoff_name;
  const draft = target && target.workflow_state !== "approved";

  return (
    <section
      data-testid="target-adopted"
      className="space-y-2 rounded border border-slate-200 bg-white p-3 text-sm"
    >
      <h2 className="text-sm font-semibold text-slate-900">{t(`${P}.heading`)}</h2>
      <p className="text-xs text-slate-600">{t(`${P}.why`)}</p>
      {!target && <p className="text-xs text-slate-700">{t(`${P}.none`)}</p>}
      {target && (
        <div className="space-y-1">
          <p data-testid="target-adopted-state">
            {t(`${P}.state.${target.workflow_state}`, {
              version: target.version,
              date: target.derived_on,
              total: formatSAR(target.total_sar),
            })}
            {target.at_least ? ` ${t(`${P}.atLeast`)}` : ""}
          </p>
          {target.rationale && (
            <p className="text-xs text-slate-600">{target.rationale}</p>
          )}
          <p className="text-xs text-slate-600">
            {t(`${P}.signatures`, {
              cro: target.cro_signoff_name ?? t(`${P}.unsigned`),
              opsRes: target.ops_res_signoff_name ?? t(`${P}.unsigned`),
            })}
          </p>
          <p
            data-testid="target-adopted-progress"
            className="font-medium text-slate-900"
          >
            {t(`${P}.progress`, {
              done: target.progress.done,
              total: target.progress.total,
              count: target.progress.total,
              sar: formatSAR(target.progress.done_sar),
            })}
          </p>
          <p className="text-xs text-slate-600">
            {t(`${P}.viability`, {
              adopted: target.progress.adopted_before_hours,
              now: target.progress.time_to_minimum_viability_hours,
              target: target.progress.adopted_target_hours,
            })}
          </p>
          <ul className="space-y-0.5 text-xs">
            {target.progress.steps.map((s, i) => (
              <li
                key={`${s.kind}-${s.target}-${i}`}
                data-testid="target-adopted-step"
                data-done={s.done}
                className={s.done ? "text-emerald-800" : "text-slate-700"}
              >
                {t(s.done ? `${P}.stepDone` : `${P}.stepOpen`, {
                  target: s.target,
                  kind: t(`${P}.kind.${s.kind}`),
                })}
              </li>
            ))}
          </ul>
          {target.workflow_state === "approved" && canPropose && (
            <button
              type="button"
              data-testid="target-pack"
              disabled={pack.isPending}
              onClick={() => pack.mutate()}
              className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1 text-xs font-medium text-brand-brassDark"
            >
              {t(`${P}.pack`)}
            </button>
          )}
          {draft && (canCro || canOps) && (
            <div className="flex flex-wrap gap-2">
              {canCro && (
                <button
                  type="button"
                  data-testid="target-sign-cro"
                  disabled={sign.isPending}
                  onClick={() => sign.mutate("cro")}
                  className="rounded bg-brand-brass px-3 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark"
                >
                  {t(`${P}.signCro`)}
                </button>
              )}
              {canOps && (
                <button
                  type="button"
                  data-testid="target-sign-ops"
                  disabled={sign.isPending}
                  onClick={() => sign.mutate("ops_res")}
                  className="rounded bg-brand-brass px-3 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark"
                >
                  {t(`${P}.signOps`)}
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {canPropose && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            propose.mutate();
          }}
        >
          <label className="text-xs text-slate-600">
            {t(`${P}.rationale`)}
            <input
              data-testid="target-rationale"
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              className="mt-1 block w-72 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <button
            type="submit"
            data-testid="target-propose"
            disabled={propose.isPending}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1 text-xs font-medium text-brand-brassDark"
          >
            {target ? t(`${P}.proposeAgain`) : t(`${P}.propose`)}
          </button>
        </form>
      )}
    </section>
  );
}
